import { useState } from "react";
import { Link } from "react-router";
import {
  ArrowLeft,
  Calendar,
  Check,
  FolderOpen,
  LayoutGrid,
  Palette,
  Rows3,
  Sparkles,
  Users,
} from "lucide-react";
import { useSetPageHeader } from "@/lib/page-header";
import { useIsMobile } from "@/lib/use-media-query";
import { useLayoutStyle, type LayoutStyle } from "@/lib/layout-style";
import { cn } from "@/lib/cn";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { CONFIG_LAYOUT_TOUR_STEPS } from "@/lib/product-tour/page-tour-steps";

type SectionKey = "menu";

type Section = {
  key: SectionKey;
  label: string;
  description: string;
  icon: React.ReactNode;
  accent: string;
};

const SECTIONS: Section[] = [
  {
    key: "menu",
    label: "Menu principal",
    description: "Densidade e estilo do menu lateral",
    icon: <LayoutGrid className="h-4 w-4" />,
    accent: "#2457ff",
  },
];

type StyleOption = {
  key: LayoutStyle;
  label: string;
  description: string;
  icon: React.ReactNode;
};

const STYLE_OPTIONS: StyleOption[] = [
  {
    key: "compact",
    label: "Compacto",
    description: "Denso — cabe mais itens sem scroll",
    icon: <Rows3 className="h-4 w-4" />,
  },
  {
    key: "classic",
    label: "Clássico",
    description: "Espaçoso, ícones maiores — padrão tradicional",
    icon: <Rows3 className="h-4 w-4" />,
  },
  {
    key: "colorful",
    label: "Colorido",
    description: "Cada item com pill colorida — estilo Linear/Notion",
    icon: <Palette className="h-4 w-4" />,
  },
];

/** `embutido`: dentro de uma seção de outra página (Configurações, Minha conta) — sem título
 *  próprio, sem "voltar" e com as subseções em abas, já que a de fora tem a barra lateral. */
export function SettingsLayout({ embutido = false }: { embutido?: boolean } = {}) {
  const compacto = useIsMobile() || embutido;
  const isMobile = useIsMobile();
  // Embutida, quem registra o tour é a página de fora (registrar aqui seria sobrescrito).
  useTourDaPagina(embutido ? [] : CONFIG_LAYOUT_TOUR_STEPS);
  useSetPageHeader(
    {
      title: isMobile ? "Layout geral" : "Configurações · Layout geral",
      subtitle: isMobile ? undefined : "Personalize a densidade e estilo da interface",
    },
    [isMobile],
    !embutido,
  );

  const [section, setSection] = useState<SectionKey>("menu");
  const active = SECTIONS.find((s) => s.key === section)!;
  const [style, setStyle] = useLayoutStyle();

  return (
    <div className={`${embutido ? "" : "mx-auto max-w-6xl "}w-full space-y-4 pb-10`}>
      {!embutido && (
        <Link
          to="/configuracoes"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Configurações
        </Link>
      )}

      {compacto ? <MobileTabs section={section} onChange={setSection} /> : null}

      <div className="flex gap-6">
        {!compacto && <DesktopSidebar section={section} onChange={setSection} />}

        <div className="min-w-0 flex-1">
          <div className="rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-border dark:bg-card">
            <div className="flex items-center gap-3 border-b border-zinc-100 px-6 py-4 dark:border-border">
              <div
                className="flex h-9 w-9 items-center justify-center rounded-lg"
                style={{
                  backgroundColor: `${active.accent}1a`,
                  color: active.accent,
                }}
              >
                {active.icon}
              </div>
              <div>
                <h2 className="text-sm font-semibold text-zinc-900 dark:text-foreground">
                  {active.label}
                </h2>
                <p className="text-xs text-zinc-500 dark:text-muted-foreground">
                  {active.description}
                </p>
              </div>
            </div>

            <div className="px-4 py-5 sm:px-6">
              <p className="mb-3 text-[11px] font-bold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
                Escolha o estilo do menu
              </p>
              <div data-tour="config-layout-estilos" className="grid gap-3 sm:grid-cols-3">
                {STYLE_OPTIONS.map((opt) => (
                  <StylePreview
                    key={opt.key}
                    option={opt}
                    active={style === opt.key}
                    onClick={() => setStyle(opt.key)}
                  />
                ))}
              </div>

              <p className="mt-4 text-[11.5px] leading-relaxed text-zinc-500 dark:text-muted-foreground">
                A mudança aplica <strong>imediatamente</strong> em todos os
                lugares onde o menu aparece. Setting salvo localmente no seu
                navegador.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Preview card de cada estilo ──────────────────────────

function StylePreview({
  option,
  active,
  onClick,
}: {
  option: StyleOption;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group relative flex flex-col gap-2 overflow-hidden rounded-xl border p-3 text-left transition-all",
        active
          ? "border-brand-500 bg-brand-50/30 shadow-md dark:border-primary dark:bg-primary/5"
          : "border-zinc-200 bg-white hover:border-brand-300 hover:shadow-sm dark:border-border dark:bg-card dark:hover:border-primary/40",
      )}
    >
      {/* Checkmark quando ativo */}
      {active && (
        <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-brand-600 text-white shadow-sm dark:bg-primary">
          <Check className="h-3 w-3" strokeWidth={3} />
        </span>
      )}

      {/* Mini preview do menu nesse estilo */}
      <div className="rounded-lg border border-zinc-100 bg-zinc-50/60 p-2 dark:border-border dark:bg-muted/20">
        <MenuPreview style={option.key} />
      </div>

      {/* Label + descrição */}
      <div className="px-0.5">
        <p className="flex items-center gap-1.5 text-[13px] font-semibold text-zinc-900 dark:text-foreground">
          {option.icon}
          {option.label}
        </p>
        <p className="mt-0.5 text-[11px] leading-snug text-zinc-500 dark:text-muted-foreground">
          {option.description}
        </p>
      </div>
    </button>
  );
}

/** Miniatura do menu — 4 linhas simulando itens reais pra o user ver
 *  exatamente o visual antes de escolher. */
function MenuPreview({ style }: { style: LayoutStyle }) {
  const items = [
    { icon: <Users className="h-3 w-3" />, label: "Pacientes", accent: "#14b8a6", active: true },
    { icon: <Calendar className="h-3 w-3" />, label: "Agenda", accent: "#0ea5e9" },
    { icon: <FolderOpen className="h-3 w-3" />, label: "Arquivos", accent: "#10b981" },
    { icon: <Sparkles className="h-3 w-3" />, label: "Apps Pro", accent: "#ec4899" },
  ];

  if (style === "colorful") {
    return (
      <div className="space-y-0.5">
        {items.map((it, i) => (
          <div
            key={i}
            className={cn(
              "flex items-center gap-2 rounded-md px-1.5 py-1 text-[10px]",
              it.active
                ? "bg-white font-semibold text-zinc-900 dark:bg-card dark:text-foreground"
                : "text-zinc-600 dark:text-muted-foreground",
            )}
          >
            <span
              className="flex h-5 w-5 items-center justify-center rounded"
              style={{ backgroundColor: `${it.accent}1a`, color: it.accent }}
            >
              {it.icon}
            </span>
            <span className="truncate">{it.label}</span>
          </div>
        ))}
      </div>
    );
  }

  if (style === "classic") {
    return (
      <div className="space-y-1">
        {items.map((it, i) => (
          <div
            key={i}
            className={cn(
              "flex items-center gap-2 rounded-md px-2 py-1.5 text-[10px]",
              it.active
                ? "bg-brand-100 font-medium text-brand-900 dark:bg-primary/15 dark:text-primary"
                : "text-zinc-600 dark:text-muted-foreground",
            )}
          >
            <span
              className={cn(
                "shrink-0",
                it.active
                  ? "text-brand-700 dark:text-primary"
                  : "text-zinc-400",
              )}
            >
              {it.icon}
            </span>
            <span className="truncate">{it.label}</span>
          </div>
        ))}
      </div>
    );
  }

  // compact
  return (
    <div className="space-y-0">
      {items.map((it, i) => (
        <div
          key={i}
          className={cn(
            "flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-[10px] leading-tight",
            it.active
              ? "bg-brand-100 font-semibold text-brand-900 dark:bg-primary/15 dark:text-primary"
              : "text-zinc-600 dark:text-muted-foreground",
          )}
        >
          <span
            className={cn(
              "shrink-0",
              it.active ? "text-brand-700 dark:text-primary" : "text-zinc-400",
            )}
          >
            {it.icon}
          </span>
          <span className="truncate">{it.label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Sidebar / tabs (mesmo padrão das outras config pages) ───

function DesktopSidebar({
  section,
  onChange,
}: {
  section: SectionKey;
  onChange: (s: SectionKey) => void;
}) {
  return (
    <aside className="w-60 shrink-0">
      <nav className="sticky top-20 space-y-1">
        {SECTIONS.map((s) => {
          const active = section === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => onChange(s.key)}
              className={`group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                active
                  ? "bg-zinc-50 text-zinc-900 dark:bg-muted/40 dark:text-foreground"
                  : "text-zinc-700 hover:bg-zinc-50 dark:text-foreground/80 dark:hover:bg-muted"
              }`}
            >
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-transform group-hover:scale-105"
                style={{
                  backgroundColor: `${s.accent}1a`,
                  color: s.accent,
                }}
              >
                {s.icon}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{s.label}</p>
                <p className="truncate text-[11px] text-zinc-500 dark:text-muted-foreground group-hover:text-zinc-600">
                  {s.description}
                </p>
              </div>
            </button>
          );
        })}
      </nav>
    </aside>
  );
}

function MobileTabs({
  section,
  onChange,
}: {
  section: SectionKey;
  onChange: (s: SectionKey) => void;
}) {
  return (
    <div className="-mx-4 overflow-x-auto border-b border-zinc-200 bg-white sm:-mx-6 dark:border-border dark:bg-card">
      <div className="flex gap-1 px-4 sm:px-6">
        {SECTIONS.map((s) => {
          const active = section === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => onChange(s.key)}
              className={`inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-xs font-medium transition-colors ${
                active
                  ? "border-brand-600 text-brand-700 dark:border-primary dark:text-primary"
                  : "border-transparent text-zinc-600 hover:text-zinc-900 dark:text-foreground/70 dark:hover:text-foreground"
              }`}
            >
              {s.icon}
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
